import React from 'react'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker'

const getInputFormat = (mode, is24hrFormat) => {
  if (mode === 'datetime') {
    return is24hrFormat ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY hh:mm A'
  }
  return 'MM/DD/YYYY'
}

/**
 * Generic DateTimePicker component that can handle both date and datetime modes
 * @param {Object} props
 * @param {string} props.mode - "date" or "datetime"
 * @param {string} props.label - Label for the picker
 * @param {string} props.name - Field name
 * @param {moment.Moment} props.value - Current value
 * @param {function} props.onChange - Change handler
 * @param {function} props.onBlur - Blur handler (optional)
 * @param {boolean} props.error - Error state
 * @param {string} props.helperText - Helper/error text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.readOnly - ReadOnly state
 * @param {boolean} props.disablePast - Disable past dates
 * @param {boolean} props.disableFuture - Disable future dates
 * @param {moment.Moment} props.minDate - Minimum selectable date
 * @param {moment.Moment} props.maxDate - Maximum selectable date
 * @param {string} props.format - Date format (optional, defaults based on mode)
 * @param {boolean} props.is24hrFormat - Use 24hr format for time (only for datetime mode)
 * @param {Object} props.textFieldProps - Additional props for the text field
 */
const CustomDateTimePicker = ({
  mode = 'date',
  label,
  name,
  value,
  onChange,
  onBlur,
  error = false,
  helperText = '',
  disabled = false,
  readOnly = true,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  is24hrFormat = false,
  textFieldProps = {},
  ...otherProps
}) => {
  // Determine the picker component and default format based on mode
  const PickerComponent = mode === 'datetime' ? DesktopDateTimePicker : DesktopDatePicker
  // const defaultFormat = mode === 'datetime' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY'

  const pickerProps = {
    value,
    label,
    name,
    onChange,
    format: getInputFormat(mode, is24hrFormat),
    disabled,
    disablePast,
    disableFuture,
    minDate,
    maxDate,
    ampm: mode === 'datetime' ? !is24hrFormat : undefined,
    closeOnSelect: mode !== 'datetime',
    slotProps: {
      textField: {
        readOnly,
        error,
        helperText,
        onBlur,
        fullWidth: true,
        variant: 'outlined',
        ...textFieldProps
      }
    },
    ...otherProps
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <PickerComponent {...pickerProps} />
    </LocalizationProvider>
  )
}

export default CustomDateTimePicker
